import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

// Upload Report Section Component
const UploadReportSection = ({ companies, token, onUploadSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [allowDownload, setAllowDownload] = useState(false);
  const [notifyUsersOnUpload, setNotifyUsersOnUpload] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    setError('');
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Por favor ingresa un título para el reporte');
      return;
    }
    
    if (!selectedCompany) {
      setError('Por favor selecciona una empresa');
      return;
    }
    
    if (files.length === 0) {
      setError('Por favor selecciona al menos un archivo');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('company_id', selectedCompany);
      formData.append('allow_download', allowDownload.toString());
      formData.append('notify_users', notifyUsersOnUpload.toString());
      
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al subir el reporte');
      }

      const result = await response.json();
      let successMsg = `Reporte "${title}" subido exitosamente. ${result.files_uploaded} archivo(s) subido(s).`;
      if (result.notifications_sent > 0) {
        successMsg += ` ${result.notifications_sent} notificación(es) enviada(s).`;
      }
      setSuccess(successMsg);
      
      // Reset form
      setTitle('');
      setDescription('');
      setSelectedCompany('');
      setAllowDownload(false);
      setNotifyUsersOnUpload(false);
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh dashboard data
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(100);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Subir Nuevo Reporte</h2>
      
      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleUpload} className="max-w-2xl mx-auto space-y-6">
          <p className="text-gray-600">Sube reportes confidenciales para tus clientes de manera segura.</p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
              {success}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título del Reporte *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Ej: Análisis de Mercado Q4 2024"
              disabled={uploading}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Descripción breve del contenido del reporte..."
              disabled={uploading}
            />
          </div>

          {/* Company Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Empresa Destinataria *
            </label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={uploading}
            >
              <option value="">Seleccionar empresa...</option>
              {companies.filter(c => c.name !== 'InsightPlace Admin').map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          {/* Allow Download Toggle */}
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="allowDownload"
              checked={allowDownload}
              onChange={(e) => setAllowDownload(e.target.checked)}
              className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
              disabled={uploading}
            />
            <label htmlFor="allowDownload" className="flex-1">
              <span className="text-sm font-medium text-gray-700">Permitir descarga</span>
              <p className="text-xs text-gray-500">
                Si está desactivado, los clientes solo podrán ver el reporte en el navegador
              </p>
            </label>
          </div>

          {/* Notify Users Toggle */}
          <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
            <input
              type="checkbox"
              id="notifyUsers"
              checked={notifyUsersOnUpload}
              onChange={(e) => setNotifyUsersOnUpload(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={uploading}
            />
            <label htmlFor="notifyUsers" className="flex-1">
              <span className="text-sm font-medium text-gray-700">Notificar a los usuarios</span>
              <p className="text-xs text-gray-500">
                Enviar un correo electrónico a todos los usuarios de la empresa notificando el nuevo reporte
              </p>
            </label>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Archivos *
            </label>
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-red-400 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept=".html,.pdf,.png,.jpg,.jpeg,.gif,.csv,.xlsx,.docx,.zip"
                disabled={uploading}
              />
              <div className="text-4xl text-gray-400 mb-2">📁</div>
              <p className="text-gray-600 mb-1">
                Haz clic para seleccionar archivos o arrastra aquí
              </p>
              <p className="text-xs text-gray-500">
                Formatos permitidos: HTML, PDF, PNG, JPG, GIF, CSV, XLSX, DOCX, ZIP
              </p>
            </div>
          </div>

          {/* Selected Files List */}
          {files.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Archivos seleccionados ({files.length})
              </h4>
              <ul className="space-y-2">
                {files.map((file, index) => (
                  <li key={index} className="flex items-center justify-between bg-white p-2 rounded-md">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">
                        {file.name.endsWith('.html') ? '🌐' :
                         file.name.endsWith('.pdf') ? '📕' :
                         file.name.match(/\.(png|jpg|jpeg|gif)$/i) ? '🖼️' :
                         file.name.match(/\.(xlsx|csv)$/i) ? '📊' :
                         file.name.endsWith('.zip') ? '📦' : '📄'}
                      </span>
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                      disabled={uploading}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-gray-500">
                Tamaño total: {formatFileSize(files.reduce((sum, file) => sum + file.size, 0))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={uploading || files.length === 0}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Subiendo...</span>
              </>
            ) : (
              <>
                <span>⬆️</span>
                <span>Subir Reporte</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showEditReportModal, setShowEditReportModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  
  // Form states
  const [newCompany, setNewCompany] = useState({ name: '', description: '' });
  const [newUser, setNewUser] = useState({ email: '', full_name: '', password: '', company_id: '', role: 'client', sendNotification: false });
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingReport, setEditingReport] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/portal');
      return;
    }
    fetchDashboardData();
  }, [isAdmin]);

  const apiCall = async (endpoint, options = {}) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error('Error en la solicitud');
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsData, companiesData, usersData, reportsData, logsData] = await Promise.all([
        apiCall('/api/admin/dashboard'),
        apiCall('/api/admin/companies'),
        apiCall('/api/admin/users'),
        apiCall('/api/admin/reports'),
        apiCall('/api/admin/activity-logs')
      ]);

      setStats(statsData);
      setCompanies(companiesData);
      setUsers(usersData);
      setReports(reportsData);
      setLogs(logsData);
    } catch (error) {
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Bogota'
    });
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    
    try {
      await apiCall('/api/admin/companies', {
        method: 'POST',
        body: JSON.stringify(newCompany)
      });
      setShowCompanyModal(false);
      setNewCompany({ name: '', description: '' });
      fetchDashboardData();
    } catch (err) {
      setModalError('Error al crear la empresa');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    
    try {
      const { sendNotification, ...userData } = newUser;
      await apiCall(`/api/admin/users?send_notification=${sendNotification}`, {
        method: 'POST',
        body: JSON.stringify(userData)
      });
      setShowUserModal(false);
      setNewUser({ email: '', full_name: '', password: '', company_id: '', role: 'client', sendNotification: false });
      fetchDashboardData();
    } catch (err) {
      setModalError('Error al crear el usuario');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${userName}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await apiCall(`/api/admin/users/${userId}`, {
        method: 'DELETE'
      });
      fetchDashboardData();
    } catch (err) {
      alert('Error al eliminar el usuario');
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`¿Estás seguro de eliminar la empresa "${companyName}"? Esto eliminará también todos los usuarios y reportes asociados. Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await apiCall(`/api/admin/companies/${companyId}`, {
        method: 'DELETE'
      });
      fetchDashboardData();
    } catch (err) {
      alert('Error al eliminar la empresa');
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    
    try {
      await apiCall(`/api/admin/companies/${editingCompany.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editingCompany.name })
      });
      setShowEditCompanyModal(false);
      setEditingCompany(null);
      fetchDashboardData();
    } catch (err) {
      setModalError('Error al actualizar la empresa');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditReport = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    
    try {
      await apiCall(`/api/admin/reports/${editingReport.id}`, {
        method: 'PUT',
        body: JSON.stringify({ 
          title: editingReport.title,
          description: editingReport.description,
          allow_download: editingReport.allow_download
        })
      });
      setShowEditReportModal(false);
      setEditingReport(null);
      fetchDashboardData();
    } catch (err) {
      setModalError('Error al actualizar el reporte');
    } finally {
      setModalLoading(false);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
            
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white border-l-4 border-red-600 rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-red-600">{stats.total_companies}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Empresas Activas</div>
                </div>
                <div className="bg-white border-l-4 border-red-600 rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-red-600">{stats.total_users}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Usuarios Totales</div>
                </div>
                <div className="bg-white border-l-4 border-red-600 rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-red-600">{stats.total_reports}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Reportes Publicados</div>
                </div>
                <div className="bg-white border-l-4 border-red-600 rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-red-600">{stats.total_access_logs}</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Registros de Acceso</div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Actividad Reciente</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {stats?.recent_activities.map((activity, index) => (
                  <div key={index} className="px-6 py-4 border-b border-gray-100 flex items-center space-x-4">
                    <div className="bg-red-100 rounded-full p-2">
                      <div className="w-4 h-4 text-red-600 text-xs flex items-center justify-center font-bold">
                        {activity.activity_type === 'login' && '👤'}
                        {activity.activity_type === 'report_upload' && '📊'}
                        {activity.activity_type === 'user_create' && '👥'}
                        {activity.activity_type === 'company_create' && '🏢'}
                        {!['login', 'report_upload', 'user_create', 'company_create'].includes(activity.activity_type) && '📋'}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{activity.description}</div>
                      <div className="text-sm text-gray-600">{formatDate(activity.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'companies':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Empresas</h2>
              <button 
                onClick={() => setShowCompanyModal(true)}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                + Agregar Empresa
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{company.name}</div>
                          {company.description && (
                            <div className="text-sm text-gray-500">{company.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(company.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {company.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button 
                          onClick={() => {
                            setEditingCompany({ id: company.id, name: company.name });
                            setShowEditCompanyModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Editar
                        </button>
                        <button 
                          onClick={() => handleDeleteCompany(company.id, company.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'users':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h2>
              <button 
                onClick={() => setShowUserModal(true)}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                + Agregar Usuario
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último Acceso</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => {
                    const userCompany = companies.find(c => c.id === user.company_id);
                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {userCompany?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {user.last_login ? formatDate(user.last_login) : 'Nunca'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {user.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2">
                          <button 
                            onClick={() => handleDeleteUser(user.id, user.full_name)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
        
      case 'reports':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Gestión de Reportes</h2>
              <button 
                onClick={() => setActiveSection('upload')}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                ⬆️ Subir Reporte
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporte</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descargas</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reports.map((report) => {
                    const reportCompany = companies.find(c => c.id === report.company_id);
                    return (
                      <tr key={report.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{report.title}</div>
                            {report.description && (
                              <div className="text-sm text-gray-500">{report.description}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {reportCompany?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(report.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {report.download_count} descargas
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2">
                          <button 
                            onClick={() => {
                              setEditingReport({ 
                                id: report.id, 
                                title: report.title, 
                                description: report.description || '',
                                allow_download: report.allow_download || false
                              });
                              setShowEditReportModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Editar
                          </button>
                          <button 
                            onClick={() => window.open(`/portal/report/${report.id}`, '_blank')}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Vista Previa
                          </button>
                          <button 
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/reports/${report.id}/download`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = report.title + '.zip';
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else {
                                  alert('Error al descargar el reporte');
                                }
                              } catch (err) {
                                alert('Error al descargar el reporte');
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Descargar
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`¿Estás seguro de eliminar el reporte "${report.title}"? Esta acción no se puede deshacer.`)) {
                                return;
                              }
                              try {
                                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/${report.id}`, {
                                  method: 'DELETE',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (response.ok) {
                                  alert('Reporte eliminado correctamente');
                                  fetchDashboardData();
                                } else {
                                  alert('Error al eliminar el reporte');
                                }
                              } catch (err) {
                                alert('Error al eliminar el reporte');
                              }
                            }}
                            className="text-red-600 hover:text-red-900 ml-3"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
        
      case 'upload':
        return <UploadReportSection companies={companies} token={token} onUploadSuccess={fetchDashboardData} />;
        
      case 'logs':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Registros de Actividad</h2>
            
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha/Hora</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.user_email || 'Desconocido'}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            log.activity_type === 'login' ? 'bg-green-100 text-green-800' :
                            log.activity_type === 'failed_login' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {log.activity_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.ip_address || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {log.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
        
      default:
        return <div>Sección no encontrada</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando panel de administración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <img src="/symbol.png" alt="InsightPlace" className="h-8 w-8" />
            <span className="font-bold text-lg">Admin Panel</span>
          </div>
        </div>
        
        <nav className="mt-6">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'companies', label: 'Empresas', icon: '🏢' },
            { id: 'users', label: 'Usuarios', icon: '👥' },
            { id: 'reports', label: 'Reportes', icon: '📁' },
            { id: 'upload', label: 'Subir Reporte', icon: '⬆️' },
            { id: 'logs', label: 'Registros', icon: '📋' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full text-left px-6 py-3 text-sm font-medium transition-colors border-l-4 ${
                activeSection === item.id
                  ? 'bg-red-50 text-red-600 border-red-600'
                  : 'text-gray-600 border-transparent hover:bg-gray-50 hover:text-red-600'
              }`}
            >
              <span className="mr-3">{item.icon}</span>
              {item.label}
            </button>
          ))}
          
          {/* User View Button */}
          <div className="mt-4 px-4">
            <Link
              to="/portal"
              className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Vista de Usuario
            </Link>
          </div>
        </nav>
        
        <div className="absolute bottom-0 w-64 p-6 border-t">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">{user?.full_name}</div>
              <div className="text-xs text-gray-600">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              Salir
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}
        {renderSection()}
      </div>

      {/* Add Company Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Agregar Nueva Empresa</h3>
              <button 
                onClick={() => { setShowCompanyModal(false); setModalError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {modalError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {modalError}
              </div>
            )}
            
            <form onSubmit={handleCreateCompany}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de la Empresa *
                  </label>
                  <input
                    type="text"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Ej: Empresa ABC S.A."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción (opcional)
                  </label>
                  <textarea
                    value={newCompany.description}
                    onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={3}
                    placeholder="Descripción breve de la empresa..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowCompanyModal(false); setModalError(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {modalLoading ? 'Creando...' : 'Crear Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Agregar Nuevo Usuario</h3>
              <button 
                onClick={() => { setShowUserModal(false); setModalError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {modalError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {modalError}
              </div>
            )}
            
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Juan Pérez"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="juan@empresa.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña *
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Mínimo 8 caracteres"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <select
                    value={newUser.company_id}
                    onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.filter(c => c.name !== 'InsightPlace Admin').map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rol
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="client">Cliente</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              </div>
              
              {/* Send Notification Checkbox */}
              <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg mt-4">
                <input
                  type="checkbox"
                  id="sendNotification"
                  checked={newUser.sendNotification}
                  onChange={(e) => setNewUser({ ...newUser, sendNotification: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="sendNotification" className="flex-1">
                  <span className="text-sm font-medium text-gray-700">Enviar credenciales por correo</span>
                  <p className="text-xs text-gray-500">
                    Enviar un correo electrónico al usuario con sus credenciales de acceso
                  </p>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowUserModal(false); setModalError(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {modalLoading ? 'Creando...' : 'Crear Usuario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompanyModal && editingCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Empresa</h3>
              <button 
                onClick={() => { setShowEditCompanyModal(false); setEditingCompany(null); setModalError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {modalError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {modalError}
              </div>
            )}
            
            <form onSubmit={handleEditCompany}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de la Empresa *
                  </label>
                  <input
                    type="text"
                    value={editingCompany.name}
                    onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Nombre de la empresa"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowEditCompanyModal(false); setEditingCompany(null); setModalError(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {modalLoading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Report Modal */}
      {showEditReportModal && editingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Reporte</h3>
              <button 
                onClick={() => { setShowEditReportModal(false); setEditingReport(null); setModalError(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {modalError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-4">
                {modalError}
              </div>
            )}
            
            <form onSubmit={handleEditReport}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título del Reporte *
                  </label>
                  <input
                    type="text"
                    value={editingReport.title}
                    onChange={(e) => setEditingReport({ ...editingReport, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Título del reporte"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={editingReport.description}
                    onChange={(e) => setEditingReport({ ...editingReport, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows={3}
                    placeholder="Descripción del reporte..."
                  />
                </div>
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="editAllowDownload"
                    checked={editingReport.allow_download}
                    onChange={(e) => setEditingReport({ ...editingReport, allow_download: e.target.checked })}
                    className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label htmlFor="editAllowDownload" className="flex-1">
                    <span className="text-sm font-medium text-gray-700">Permitir descarga</span>
                    <p className="text-xs text-gray-500">
                      Si está desactivado, los clientes solo podrán ver el reporte en el navegador
                    </p>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowEditReportModal(false); setEditingReport(null); setModalError(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {modalLoading ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
